import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { ApiService, Device, DevicesService } from '@nrwl/common-lib';
import { first, takeUntil } from 'rxjs/operators';

export const TWAMP_MODE = {
  FULL: 'twamp-reflector',
  LIGHT: 'twamp-light-reflector',
  PING: 'twamp-ping-reflector'
};

@Injectable({
  providedIn: 'root'
})
export class TwampManagedDevicesService {
  twampRunningReflectorsPath =
    '/sbi/restconf/data/v2/list?scheduleid=all&status=running&type=twamp-reflector,twamp-light-reflector,';
  //eslint-disable-next-line
  realtimemonitorcache: Array<any> = [];
  //eslint-disable-next-line
  managedDevicesArr: Array<any> = [];
  cache = new BehaviorSubject([]);
  //eslint-disable-next-line
  updated: Subject<Array<any>>;
  queryLoading = false;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    private readonly apiService: ApiService,
    private readonly devicesService: DevicesService
  ) {}

  getEntityName(): string {
    return 'managed-devices';
  }

  getValues(): Array<[]> {
    return this.getManagedDevices();
  }

  getValuesAsync(): Observable<Array<[]>> {
    const devices = this.getValues();
    if (!devices || devices.length === 0) {
      return this.managedDevices(this.realtimemonitorcache).pipe(first());
    }
    return of(devices).pipe(first());
  }

  getManagedDevices(): Array<[]> {
    return this.chooseCacheModel();
  }

  chooseCacheModel(): Array<[]> {
    // simplified implementation/no support for history mode
    return this.realtimemonitorcache;
  }

  //eslint-disable-next-line
  queryDevices(data): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(this.twampRunningReflectorsPath, data);
  }
  //eslint-disable-next-line
  managedDevices(cache: any): any {
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.queryLoading = true;
    this.queryDevices({})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        reflectors => {
          this.queryLoading = false;
          if (reflectors && reflectors.schedules.length >= 0) {
            const runningReflectorsRes = reflectors;
            this.devicesService.cache.subscribe(devices => {
              const deviceResponse = devices;
              const deviceList = this.runningReflectorsForAgent(
                deviceResponse,
                runningReflectorsRes
              );
              this.assignManagedDevices(cache, deviceList);
              this.updated.next(this.getManagedDevices());
              return this.chooseCacheModel();
            });
            return this.updated;
          }
        },
        () => {
          this.queryLoading = false;
        },
        () => {
          this.queryLoading = false;
          this.updated.complete();
        }
      );
    return this.updated;
  }
  //eslint-disable-next-line
  runningReflectorsForAgent(devices: Array<Device>, reflectors: object): any {
    if (devices) {
      const runningReflectors = reflectors['schedules'];
      this.managedDevicesArr = [];
      devices?.forEach(device => {
        const reflectorArr = [];
        if (device.deviceData.model.model.indexOf('JMEP') === -1) {
          runningReflectors?.forEach(reflector => {
            const reflectorMsg = this.isJSON(reflector.msg) ? JSON.parse(reflector.msg) : {};
            if (
              device['deviceData'].nameId === reflector.MaId &&
              device['deviceData'].phyId === reflector.phyId
            ) {
              if (reflectorMsg['Control Server Port'] || reflectorMsg['reflector Port']) {
                reflectorArr.push({
                  name: reflector.name,
                  ip: reflectorMsg['Control Server IP'] ?? reflectorMsg['reflector IP'],
                  port: reflectorMsg['Control Server Port'] ?? reflectorMsg['reflector Port'],
                  type: reflector.type,
                  phyId: reflector.phyId,
                  phyName: reflector.phyName,
                  twampMode:
                    reflector.type === 'twamp-reflector' ? TWAMP_MODE.FULL : TWAMP_MODE.LIGHT,
                  reservedFlows: this.getCapability(
                    device.deviceData.capabilities,
                    reflector.phyId,
                    device.deviceData.model.model === 'QT600-10'
                      ? 'max-flows-per-port'
                      : 'max-flows-per-test'
                  )
                });
              } else {
                // eslint-disable-next-line
                console.info(
                  'Skipping reflector ' + reflector.MaId + '- Agent version not supported'
                );
              }
            }
            device.deviceData['running-reflectors'] = reflectorArr;
          });
          this.managedDevicesArr.push(device);
        } else if (device.deviceData['capabilities'].simpleTestConfig?.twampReflector === 'true') {
          reflectorArr.push({
            ip:
              device.deviceData['capabilities'].phy['test-ip'] &&
              device.deviceData['capabilities'].phy['test-ip'] !== '0.0.0.0'
                ? device.deviceData['capabilities'].phy['test-ip']
                : device.deviceData['capabilities'].phy['assigned-ip'],
            port: device.deviceData['capabilities'].simpleTestConfig['twamp-udp-port'],
            type: 'twamp-light-reflector',
            twampMode: TWAMP_MODE.LIGHT,
            reservedFlows: this.getCapability(
              device.deviceData.capabilities,
              'p1',
              'max-flows-per-test'
            )
          });
          device.deviceData['capabilities'] = {
            ...device.deviceData['capabilities'],
            'twamp-light-reflector': {
              'udp-port': device.deviceData['capabilities'].simpleTestConfig['twamp-udp-port'],
              ...device.deviceData['capabilities']['twamp-light-reflector']
            }
          };
          device.deviceData['running-reflectors'] = reflectorArr;
          this.managedDevicesArr.push(device);
        }
      });
      return this.managedDevicesArr;
    }
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
  //eslint-disable-next-line
  assignManagedDevices(cache: any, data: Array<any>): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of data) {
      cache.push(entry);
    }

    this.cache.next(this.chooseCacheModel());
  }
  //eslint-disable-next-line
  getCapability(capabilities, phyId, key) {
    if (capabilities && capabilities['twamp-initiator-list']?.length > 0) {
      const cap = capabilities['twamp-initiator-list']?.find(item => item['phy-id'] === phyId);
      if (cap) {
        return cap[key];
      }
    }
  }
  isJSON(text: string): boolean {
    if (typeof text !== 'string') {
      return false;
    }
    try {
      JSON.parse(text);
      return true;
    } catch (error) {
      return false;
    }
  }
}
